// ConvoCX unified inbox — command bar + keyboard shortcuts (CON-845).
// A CommandPalette bound to Cmd/Ctrl-K with navigation, filter, status, and
// assignment actions, plus a global key handler for quick actions.

import { useEffect, useMemo, useState } from 'react';
import { CommandPalette, Kbd, createStaticSource, type SearchableItem } from '@astryxdesign/core';
import type { Conversation, ConversationStatus, TeamMember } from './types';
import type { ChannelFilter, StatusFilter } from './useInbox';

interface CommandItem extends SearchableItem<{ group: string }> {
  run: () => void;
}

export interface CommandActions {
  setStatusFilter: (s: StatusFilter) => void;
  setChannelFilter: (c: ChannelFilter) => void;
  selectConversation: (id: string) => void;
  setStatus: (s: ConversationStatus) => void;
  assignTo: (userId: string | null) => void;
  toggleStar: () => void;
  toggleArchive: () => void;
  focusSearch: () => void;
}

interface CommandBarProps {
  conversations: Conversation[];
  selectedId: string | null;
  team: TeamMember[];
  actions: CommandActions;
  enabled?: boolean;
}

const STATUSES: ConversationStatus[] = ['open', 'pending', 'resolved'];

export function CommandBar({ conversations, selectedId, team, actions, enabled = true }: CommandBarProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Cmd/Ctrl+K to open the palette.
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled]);

  // Global quick shortcuts.
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isTyping = ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable;
      if (isTyping || e.metaKey || e.ctrlKey || e.altKey) return;
      const key = e.key.toLowerCase();
      if (key === 's') {
        e.preventDefault();
        actions.setStatusFilter('open');
      } else if (key === 'r') {
        e.preventDefault();
        actions.setStatusFilter('resolved');
      } else if (key === 'u') {
        e.preventDefault();
        actions.setStatusFilter('unassigned');
      } else if (key === '/') {
        e.preventDefault();
        actions.focusSearch();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [actions, enabled]);

  const items = useMemo<CommandItem[]>(() => {
    const result: CommandItem[] = [];

    for (const s of STATUSES) {
      result.push({
        id: `status-${s}`,
        label: `Filter: ${s} conversations`,
        auxiliaryData: { group: 'Filters' },
        run: () => actions.setStatusFilter(s),
      });
    }
    result.push({
      id: 'status-all',
      label: 'Filter: all conversations',
      auxiliaryData: { group: 'Filters' },
      run: () => actions.setStatusFilter('all'),
    });

    for (const conv of conversations.slice(0, 15)) {
      const name = (conv.participant_data as { name?: string })?.name || conv.subject || `Conversation ${conv.id.slice(0, 6)}`;
      result.push({
        id: `conv-${conv.id}`,
        label: `Open: ${name}`,
        auxiliaryData: { group: 'Conversations' },
        run: () => actions.selectConversation(conv.id),
      });
    }

    if (selectedId) {
      for (const s of STATUSES) {
        result.push({
          id: `set-${s}`,
          label: `Set status: ${s}`,
          auxiliaryData: { group: 'Selected conversation' },
          run: () => actions.setStatus(s),
        });
      }
      result.push({
        id: 'toggle-star',
        label: 'Toggle star',
        auxiliaryData: { group: 'Selected conversation' },
        run: actions.toggleStar,
      });
      result.push({
        id: 'toggle-archive',
        label: 'Toggle archive',
        auxiliaryData: { group: 'Selected conversation' },
        run: actions.toggleArchive,
      });
    }

    for (const m of team.slice(0, 10)) {
      result.push({
        id: `assign-${m.id}`,
        label: `Assign to: ${m.displayName}`,
        auxiliaryData: { group: 'Assignment' },
        run: () => actions.assignTo(m.id),
      });
    }

    return result;
  }, [conversations, selectedId, team, actions]);

  const source = useMemo(() => createStaticSource(items, { keywords: (i) => [i.auxiliaryData?.group ?? ''] }), [items]);

  return (
    <>
      <CommandPalette
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        searchSource={source}
        renderItem={(item) => (
          <span>
            <span style={{ color: 'var(--color-text-secondary)' }}>{item.auxiliaryData?.group}: </span>
            {item.label}
          </span>
        )}
      />
      <div className="ccx-shortcuts" data-testid="inbox-shortcuts" aria-label="Keyboard shortcuts">
        <span><Kbd keys="mod+k" /> Command palette</span>
        <span><Kbd keys="s" /> Open</span>
        <span><Kbd keys="r" /> Resolved</span>
        <span><Kbd keys="u" /> Unassigned</span>
        <span><Kbd keys="/" /> Search</span>
      </div>
    </>
  );
}